import { useState, useRef, useCallback, useEffect } from "react";

interface HandState {
  angle: number;
  value: number;
}

export default function ClockSection() {
  const [isNight, setIsNight] = useState(false);
  const [hourHand, setHourHand] = useState<HandState>({ angle: 300, value: 10 });
  const [minuteHand, setMinuteHand] = useState<HandState>({
    angle: 60,
    value: 10,
  });
  const [dragging, setDragging] = useState<"hour" | "minute" | null>(null);

  const clockRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback(
    (hand: "hour" | "minute", e: React.MouseEvent) => {
      e.preventDefault();
      setDragging(hand);
    },
    []
  );

  const handleTouchStart = useCallback(
    (hand: "hour" | "minute", e: React.TouchEvent) => {
      e.preventDefault();
      setDragging(hand);
    },
    []
  );

  const updateHandFromEvent = useCallback(
    (clientX: number, clientY: number) => {
      if (!clockRef.current || !dragging) return;

      const rect = clockRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const dx = clientX - centerX;
      const dy = clientY - centerY;
      let angle = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
      if (angle < 0) angle += 360;

      if (dragging === "hour") {
        const hourValue = Math.round(angle / 30);
        setHourHand({
          angle: hourValue * 30,
          value: hourValue === 0 ? 12 : hourValue,
        });
      } else {
        const minuteValue = Math.round(angle / 6);
        setMinuteHand({
          angle: minuteValue * 6,
          value: minuteValue === 60 ? 0 : minuteValue,
        });
      }
    },
    [dragging]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragging) return;
      updateHandFromEvent(e.clientX, e.clientY);
    },
    [dragging, updateHandFromEvent]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();
      if (!dragging) return;
      const touch = e.touches[0];
      updateHandFromEvent(touch.clientX, touch.clientY);
    },
    [dragging, updateHandFromEvent]
  );

  const handleEnd = useCallback(() => {
    setDragging(null);
  }, []);

  // Generate tick marks
  const ticks = Array.from({ length: 60 }, (_, i) => {
    const isHour = i % 5 === 0;
    const isCardinal = i % 15 === 0;
    return { index: i, isHour, isCardinal };
  });

  return (
    <section
      id="horloge"
      className={`relative min-h-screen flex items-center justify-center py-20 transition-all duration-700 ${
        isNight
          ? "bg-[#1F1F1F]"
          : "bg-gradient-to-b from-white via-stone-50 to-stone-100"
      }`}
    >
      {/* Night glow effect around clock */}
      {isNight && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-white/8 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[650px] h-[650px] bg-amber-100/12 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[480px] h-[480px] bg-white/15 rounded-full blur-2xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[360px] h-[360px] bg-amber-50/20 rounded-full blur-xl" />
        </div>
      )}

      <div className="w-full px-6 md:px-10 lg:px-16">
        {/* Toggle */}
        <div className="flex justify-center mb-12">
          <button
            onClick={() => setIsNight(!isNight)}
            className="relative w-36 h-16 rounded-full border-3 flex items-center transition-all duration-500 cursor-pointer"
            style={{
              borderWidth: "3px",
              borderStyle: "solid",
              borderColor: isNight ? "#3a3a3a" : "#d4d4d4",
              backgroundColor: isNight ? "#1F1F1F" : "#f0f0f0",
              boxShadow: isNight
                ? "inset 0 2px 8px rgba(0,0,0,0.5), 0 2px 6px rgba(0,0,0,0.3)"
                : "inset 0 2px 8px rgba(0,0,0,0.1), 0 2px 6px rgba(0,0,0,0.1)",
            }}
          >
            {/* Sun icon (left side) */}
            <div
              className="absolute left-4 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center transition-all duration-500 z-10"
              style={{
                opacity: isNight ? 0.2 : 1,
                filter: isNight ? "grayscale(100%)" : "none",
              }}
            >
              <i
                className="ri-sun-line text-2xl"
                style={{
                  color: isNight ? "#666" : "#E8913A",
                  textShadow: isNight ? "none" : "0 0 12px rgba(232,145,58,0.8), 0 0 24px rgba(232,145,58,0.4)",
                }}
              />
            </div>

            {/* Moon icon (right side) */}
            <div
              className="absolute right-4 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center transition-all duration-500 z-10"
              style={{
                opacity: isNight ? 1 : 0.2,
                filter: isNight ? "none" : "grayscale(100%)",
              }}
            >
              <i
                className="ri-moon-line text-2xl"
                style={{
                  color: isNight ? "#fff" : "#999",
                  textShadow: isNight ? "0 0 12px rgba(255,255,255,0.8), 0 0 24px rgba(255,255,255,0.4)" : "none",
                }}
              />
            </div>

            {/* Sliding button covers inactive icon */}
            <div
              className="absolute w-11 h-11 rounded-full transition-all duration-500 z-20 flex items-center justify-center"
              style={{
                left: isNight ? "6px" : "calc(100% - 50px)",
                backgroundColor: isNight ? "#2a2a2a" : "#fff",
                boxShadow: isNight
                  ? "0 2px 8px rgba(0,0,0,0.5), inset 0 1px 2px rgba(255,255,255,0.05)"
                  : "0 2px 8px rgba(0,0,0,0.2), inset 0 1px 2px rgba(255,255,255,0.8)",
              }}
            />
          </button>
        </div>

        {/* Clock container */}
        <div className="flex justify-center">
          <div
            ref={clockRef}
            className="relative w-[320px] h-[320px] md:w-[400px] md:h-[400px] lg:w-[480px] lg:h-[480px] rounded-full select-none touch-none"
            onMouseMove={handleMouseMove}
            onMouseUp={handleEnd}
            onMouseLeave={handleEnd}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleEnd}
          >
            {/* Clock face background - always white */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                backgroundColor: "#f5f5f5",
                boxShadow: isNight
                  ? "inset 0 4px 20px rgba(0,0,0,0.15), 0 0 80px rgba(255, 255, 255, 0.25), 0 0 160px rgba(255, 200, 100, 0.15), 0 8px 32px rgba(0,0,0,0.4)"
                  : "inset 0 4px 20px rgba(0,0,0,0.08), 0 8px 32px rgba(0,0,0,0.12), inset 0 -2px 8px rgba(255,255,255,0.8)",
              }}
            >
              {/* Inner rim */}
              <div
                className="absolute inset-3 rounded-full border-2"
                style={{
                  borderColor: "rgba(0,0,0,0.06)",
                }}
              />
            </div>

            {/* Tick marks */}
            {ticks.map((tick) => {
              const angle = tick.index * 6;
              const rad = (angle - 90) * (Math.PI / 180);
              const radius = 50; // percentage from center
              const x = 50 + radius * Math.cos(rad);
              const y = 50 + radius * Math.sin(rad);

              return (
                <div
                  key={tick.index}
                  className="absolute"
                  style={{
                    left: `${x}%`,
                    top: `${y}%`,
                    transform: `translate(-50%, -50%) rotate(${angle}deg)`,
                  }}
                >
                  <div
                    className="rounded-full"
                    style={{
                      width: tick.isCardinal ? "3px" : tick.isHour ? "2px" : "1px",
                      height: tick.isCardinal ? "18px" : tick.isHour ? "14px" : "8px",
                      backgroundColor: "#333",
                      opacity: tick.isHour ? 1 : 0.5,
                    }}
                  />
                </div>
              );
            })}

            {/* Hour numbers - 1-12 in day mode, 13-24 in night mode */}
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((num, index) => {
              const angle = num * 30 - 90;
              const rad = (angle * Math.PI) / 180;
              const radius = 38; // percentage from center
              const x = 50 + radius * Math.cos(rad);
              const y = 50 + radius * Math.sin(rad);
              const displayNum = isNight ? (num === 12 ? 0 : num + 12) : num;

              return (
                <div
                  key={num}
                  className="absolute font-display text-lg md:text-xl lg:text-2xl font-bold"
                  style={{
                    left: `${x}%`,
                    top: `${y}%`,
                    transform: "translate(-50%, -50%)",
                    color: "#2D2D2D",
                  }}
                >
                  {displayNum}
                </div>
              );
            })}

            {/* Minute numbers (smaller, every 5) */}
            {[5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60].map((num) => {
              const angle = num * 6 - 90;
              const rad = (angle * Math.PI) / 180;
              const radius = 44;
              const x = 50 + radius * Math.cos(rad);
              const y = 50 + radius * Math.sin(rad);

              return (
                <div
                  key={num}
                  className="absolute font-mono text-xs"
                  style={{
                    left: `${x}%`,
                    top: `${y}%`,
                    transform: "translate(-50%, -50%)",
                    color: "rgba(0,0,0,0.25)",
                  }}
                >
                  {num === 60 ? 0 : num}
                </div>
              );
            })}

            {/* Center dot */}
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full z-20"
              style={{
                backgroundColor: "#2D2D2D",
                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
              }}
            />

            {/* Hour hand - interactive */}
            <div
              className="absolute left-1/2 top-1/2 z-10 cursor-grab active:cursor-grabbing"
              style={{
                transform: `translate(-50%, -100%) rotate(${hourHand.angle}deg)`,
                transformOrigin: "50% 100%",
                height: "28%",
                width: "6px",
              }}
              onMouseDown={(e) => handleMouseDown("hour", e)}
              onTouchStart={(e) => handleTouchStart("hour", e)}
            >
              <div
                className="w-full h-full rounded-full"
                style={{
                  backgroundColor: "#2D2D2D",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
              />
            </div>

            {/* Minute hand - interactive */}
            <div
              className="absolute left-1/2 top-1/2 z-10 cursor-grab active:cursor-grabbing"
              style={{
                transform: `translate(-50%, -100%) rotate(${minuteHand.angle}deg)`,
                transformOrigin: "50% 100%",
                height: "40%",
                width: "4px",
              }}
              onMouseDown={(e) => handleMouseDown("minute", e)}
              onTouchStart={(e) => handleTouchStart("minute", e)}
            >
              <div
                className="w-full h-full rounded-full"
                style={{
                  backgroundColor: "#555",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
              />
            </div>

            {/* Second hand (non-interactive, real time) */}
            <SecondHand isNight={isNight} />
          </div>
        </div>

        {/* Instructions */}
        <p
          className={`text-center mt-16 text-sm transition-colors duration-500 ${
            isNight ? "text-white/50" : "text-brand-muted"
          }`}
        >
          <i className="ri-drag-move-line mr-1" />
          Glissez les aiguilles pour régler l'heure
        </p>
      </div>
    </section>
  );
}

function SecondHand({ isNight }: { isNight: boolean }) {
  const [angle, setAngle] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const seconds = now.getSeconds();
      const ms = now.getMilliseconds();
      setAngle(seconds * 6 + ms * 0.006);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="absolute left-1/2 top-1/2 z-10"
      style={{
        transform: `translate(-50%, -100%) rotate(${angle}deg)`,
        transformOrigin: "50% 100%",
        height: "42%",
        width: "1.5px",
      }}
    >
      <div
        className="w-full h-full rounded-full"
        style={{
          backgroundColor: "#E8913A",
          opacity: 0.8,
        }}
      />
    </div>
  );
}